import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { VacationCard } from "./VacationCard";
import { VacationCardSkeleton } from "./VacationCardSkeleton";
import { ScrollToTop } from "./ScrollToTop";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Search, Filter } from "lucide-react";
import { User } from "@supabase/supabase-js";
import { toast } from "sonner";
import { VacationMascot } from "./VacationMascot";
import { useInfiniteScroll } from "@/hooks/useInfiniteScroll";

interface RondreizenProps {
  onBack: () => void;
  user?: User | null;
  favorites?: number[];
  onFavoriteChange?: () => void;
}

export const Rondreizen = ({ onBack, user = null, favorites = [], onFavoriteChange = () => {} }: RondreizenProps) => {
  const [vacations, setVacations] = useState<any[]>([]);
  const [displayCount, setDisplayCount] = useState(20);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [countryFilter, setCountryFilter] = useState("all");
  const [priceFilter, setPriceFilter] = useState('all');
  const [countries, setCountries] = useState<string[]>([]);

  const hasMoreResults = displayCount < vacations.length;
  const displayedVacations = vacations.slice(0, displayCount);

  const { shouldLoadMore, resetLoadMore } = useInfiniteScroll({
    hasNextPage: hasMoreResults,
    isLoading: loading
  });

  useEffect(() => {
    if (shouldLoadMore) {
      setDisplayCount(prev => Math.min(prev + 20, vacations.length));
      resetLoadMore();
    }
  }, [shouldLoadMore, vacations.length, resetLoadMore]);

  useEffect(() => {
    fetchCountries();
  }, []);

  const fetchCountries = async () => {
    try {
      const { data, error } = await supabase
        .from('TUI')
        .select('country')
        .not('country', 'is', null)
        .eq('rondreizen', true);
      
      if (error) throw error;
      
      const uniqueCountries = [...new Set(data.map(item => item.country))].sort();
      setCountries(uniqueCountries);
    } catch (error: any) {
      console.error('Error fetching countries:', error);
    }
  };

  useEffect(() => {
    const delayedSearch = setTimeout(() => {
      fetchVacations();
    }, 300);

    return () => clearTimeout(delayedSearch);
  }, [searchTerm, countryFilter, priceFilter]);

  const fetchVacations = async () => {
    setLoading(true);
    try {
      let query = supabase
        .from('TUI')
        .select('*')
        .not('imageURL', 'is', null)
        .not('name', 'is', null)
        .eq('rondreizen', true)
        .order('price', { ascending: true });

      if (searchTerm) {
        query = query.or(`name.ilike.%${searchTerm}%,description.ilike.%${searchTerm}%,city.ilike.%${searchTerm}%`);
      }

      if (countryFilter !== "all") {
        query = query.eq('country', countryFilter);
      }

      if (priceFilter === '500') {
        query = query.lt('price', 500);
      } else if (priceFilter === '1000') {
        query = query.gte('price', 500).lt('price', 1000);
      } else if (priceFilter === '2000') {
        query = query.gte('price', 1000).lt('price', 2000);
      } else if (priceFilter === '3500') {
        query = query.gte('price', 2000).lt('price', 3500);
      } else if (priceFilter === '5000') {
        query = query.gte('price', 3500).lt('price', 5000);
      } else if (priceFilter === '5000+') {
        query = query.gte('price', 5000);
      }

      const { data, error } = await query.limit(50);
      
      if (error) throw error;
      
      setVacations(data || []);
    } catch (error: any) {
      toast.error("Fout bij het laden van rondreizen");
      console.error('Error fetching rondreizen:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="space-y-4">
        <Button variant="outline" onClick={onBack} className="flex items-center gap-2 w-full md:w-auto">
          <ArrowLeft className="h-4 w-4" />
          Terug naar zoeken
        </Button>
        
        <div className="flex items-center justify-center gap-4 sm:gap-6">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold bg-gradient-to-r from-blue-600 via-indigo-500 to-purple-500 bg-clip-text text-transparent">
            Rondreizen
          </h2>
          <div className="flex-shrink-0">
            <VacationMascot size="small" />
          </div>
        </div>
      </div>
      
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-xl border border-blue-200">
        <p className="text-blue-800 text-center font-medium text-lg">
          🗺️ Ontdek de mooiste rondreizen! Avontuurlijke reizen vol ontdekkingen
        </p>
      </div>

      <div className="bg-white/80 backdrop-blur-sm p-6 rounded-xl shadow-lg border border-blue-100 space-y-4">
        <div className="flex items-center gap-2 mb-4">
          <Filter className="h-5 w-5 text-blue-600" />
          <span className="font-semibold text-gray-800">Populair:</span>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Zoek op naam of locatie..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 border-blue-200 focus:border-blue-400"
            />
          </div>
          
          <Select value={countryFilter} onValueChange={setCountryFilter}>
            <SelectTrigger className="border-blue-200 focus:border-blue-400">
              <SelectValue placeholder="Selecteer land" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle landen</SelectItem>
              {countries.map((country) => (
                <SelectItem key={country} value={country}>
                  {country}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Select value={priceFilter} onValueChange={setPriceFilter}>
            <SelectTrigger className="border-blue-200 focus:border-blue-400">
              <SelectValue placeholder="Prijsklasse" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle prijzen</SelectItem>
              <SelectItem value="500">Tot €500</SelectItem>
              <SelectItem value="1000">€500 - €1000</SelectItem>
              <SelectItem value="2000">€1000 - €2000</SelectItem>
              <SelectItem value="3500">€2000 - €3500</SelectItem>
              <SelectItem value="5000">€3500 - €5000</SelectItem>
              <SelectItem value="5000+">Vanaf €5000</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      
      {loading ? (
        <>
          <div className="text-center py-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-2 text-gray-600">Rondreizen laden...</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {Array.from({ length: 9 }).map((_, index) => (
              <VacationCardSkeleton key={`skeleton-${index}`} />
            ))}
          </div>
        </>
      ) : (
        <>
          <div className="text-sm text-gray-600">
            {displayedVacations.length} van {vacations.length} {vacations.length === 1 ? 'rondreis' : 'rondreizen'} getoond
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {displayedVacations.map((vacation) => (
              <VacationCard
                key={vacation['product ID']}
                vacation={vacation}
                user={user}
                isFavorite={favorites.includes(vacation['product ID'])}
                onFavoriteChange={onFavoriteChange}
              />
            ))}
          </div>

          {hasMoreResults && (
            <div className="text-center text-sm text-gray-500 py-4">
              Scroll naar beneden voor meer rondreizen...
            </div>
          )}

          <ScrollToTop />
        </>
      )}
    </div>
  );
};
